/* eslint-disable @typescript-eslint/naming-convention */
import { Account, ProductSelectionPresetSearchResult, ProductSelectionResult, PsLangs } from '@brenger/api-client';
import { useDebounce } from '@brenger/react';
import axios, { CancelTokenSource } from 'axios';
import cn from 'classnames';
import React from 'react';

import { Translate } from 'react-localize-redux-dep-updated';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import '../../../../assets/product-selection/search.scss';
import { IconSmile } from '../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../hooks';
import { r } from '../../../../routes';
import { CacheKey } from '../../../../types';
import { logError } from '../../../../utils/basics';
import { trackEvent } from '../../../../utils/eventTracking';
import { getActiveLanguageSettings } from '../../../../utils/localization';
import { priceClient } from '../../../../utils/request';
import { getWhatIsMyCountry } from '../../../User/ducks';
import { actions } from '../../ducks';
import { MANUAL_IRI } from './ItemConfig';
import { SearchListItem } from './SearchListItem';
import { SearchListZeroResults } from './SearchListZeroResults';

export const SearchList: React.FC = () => {
  const capTotalElements = 7; // Number of elements we can see in the list for the first time without loading more elements
  const history = useHistory();
  const dispatch = useDispatch();
  const [showAll, setShowAll] = React.useState(false);
  const queryVal = useTypedSelector(state => state.generalTransport.product_selection.query);
  const dbQueryVal = useDebounce(queryVal || '', 400);
  const accountId = useTypedSelector(state => (state.user.userData?.account as Account)?.id);
  /**
   * To keep track of search attempts, we store attempts and the query in a ref
   */
  const attemptsRef = React.useRef(1);
  const prevDbQueryValRef = React.useRef(dbQueryVal);
  React.useEffect(() => {
    const prevDbQueryVal = prevDbQueryValRef.current;
    const attempts = attemptsRef.current;
    let isAttempt = false;

    if (dbQueryVal.indexOf(prevDbQueryVal) === -1) {
      // User started over
      isAttempt = true;
    } else if (prevDbQueryVal.length > dbQueryVal.length + 2) {
      // User backspaced more then 3 characters
      isAttempt = true;
    }
    if (isAttempt) {
      attemptsRef.current = attempts + 1;
    }
    prevDbQueryValRef.current = dbQueryVal;
  }, [dbQueryVal]);

  const queryValMin = dbQueryVal.length > 2;
  let locale = getActiveLanguageSettings().labels.full.replace('-', '_') as PsLangs;
  const countryByIp = useTypedSelector(getWhatIsMyCountry);
  // At pricing we support flemish as lang
  // Search results are really important and will match better with for some items
  if (locale === 'nl_NL' && countryByIp === 'BE') {
    locale = 'nl_BE';
  }

  const { data: results, isLoading } = useQuery({
    queryKey: [CacheKey.RETRIEVE_PS_SEARCH, dbQueryVal],
    queryFn: () => search(dbQueryVal),
  });

  const hasResults = Boolean(results?.groups?.length || results?.presets?.length);

  React.useEffect(() => {
    if (queryValMin && !hasResults) {
      trackEvent({
        event: 'productSelection',
        action: 'zero-results',
        query: dbQueryVal,
        id: '',
        listLength: 0,
        listPosition: 0,
        attempts: attemptsRef.current,
      });
    }
  }, [queryValMin, hasResults]);

  const search = async (
    query: string
  ): Promise<{
    groups: (ProductSelectionResult & { label: string })[];
    presets: (ProductSelectionPresetSearchResult & { label: string })[];
  }> => {
    setShowAll(false);
    if (!dbQueryVal || !queryValMin) {
      return { groups: [], presets: [] };
    }
    try {
      /**
       * GET RESULTS
       */
      const [groups, presets] = await Promise.all([
        productSelectionSearch(query),
        productSelectionPresetsSearch(query, accountId).catch(() => []),
      ]);

      // Enrich results with an html label
      const groupsWithLabels = groups.map(group => {
        return {
          ...group,
          label: addBoldToLabel(group.name[locale], dbQueryVal),
        };
      });
      const presetsWithLabels = presets?.map(preset => {
        return {
          ...preset,
          label: addBoldToLabel(preset.title, dbQueryVal),
        };
      });
      return {
        groups: groupsWithLabels,
        presets: presetsWithLabels,
      };
    } catch (e) {
      logError(e);
      return { groups: [], presets: [] };
    }
  };

  if (isLoading) {
    return (
      <div className="ps-modal--section">
        <IconSmile spinning={true} />
      </div>
    );
  }

  const selectResult = (result: Partial<ProductSelectionResult> | ProductSelectionPresetSearchResult): void => {
    dispatch(actions.setSearchResultPs(result));
    history.replace(r.generalFlow.items.configure());
  };

  const visibleResults = (showAll ? results?.groups : results?.groups?.slice(0, capTotalElements)) || [];
  return (
    <div className="ps-search">
      {queryValMin && hasResults && (
        <>
          {results?.presets && results?.presets.length > 0 && (
            <>
              {results?.presets.map((preset, i) => {
                const eventData = {
                  listPosition: i + 1,
                  listLength: results?.presets?.length || 0,
                  query: dbQueryVal,
                  id: preset.preset_id,
                  result: preset,
                };
                return (
                  <SearchListItem
                    key={i}
                    {...eventData}
                    locale={locale}
                    bordered={false}
                    onClick={() => {
                      selectResult(preset);
                      trackEvent({
                        event: 'productSelection',
                        action: 'click',
                        attempts: attemptsRef.current,
                        ...eventData,
                      });
                    }}
                  />
                );
              })}
            </>
          )}
          {visibleResults.map((result, i) => {
            const eventData = {
              listPosition: i + 1,
              listLength: visibleResults.length,
              query: dbQueryVal,
              id: result.iri,
              result,
            };
            return (
              <SearchListItem
                key={i}
                {...eventData}
                locale={locale}
                bordered={visibleResults.length - 1 !== i}
                onClick={() => {
                  selectResult(result);
                  // TODO Let's do it like this for now but Paul will refactor it
                  trackEvent({
                    event: 'productSelection',
                    action: 'click',
                    attempts: attemptsRef.current,
                    ...eventData,
                  });
                }}
              />
            );
          })}
          {!showAll && (results?.groups || []).length > capTotalElements && (
            <div
              className="ps-search--load-more bp bordered ps-modal--section cursor-pointer trigger text-primary"
              onClick={() => setShowAll(true)}
            >
              <Translate id={'request_flow.product_selection.load_more'} />
            </div>
          )}
          <div
            className={cn('ps-search--add-manual ps-modal--section bp cursor-pointer trigger', {
              'border-top': showAll || results?.groups?.length || 0 < capTotalElements,
            })}
            onClick={() => {
              trackEvent({
                event: 'productSelection',
                action: 'manual',
                query: dbQueryVal,
                id: MANUAL_IRI,
                listLength: visibleResults.length,
                listPosition: 0,
                attempts: attemptsRef.current,
              });
              selectResult({ iri: MANUAL_IRI });
            }}
          >
            <Translate id={'request_flow.product_selection.search_manual_item'} />{' '}
            <IconSmile smileDirection={'right'} />
          </div>
        </>
      )}
      {queryValMin && !hasResults && (
        <>
          <div className="ps-modal--section bordered bp cursor-pointer trigger">
            <h6 className="mb-1">
              <Translate id={'request_flow.product_selection.search_zero_results'} data={{ searchQuery: queryVal }} />
            </h6>
            <i>
              <Translate id={'request_flow.product_selection.search_hint'} />
            </i>
          </div>
          <div
            className="ps-search--add-manual ps-modal--section bordered bp cursor-pointer trigger"
            onClick={() => {
              trackEvent({
                event: 'productSelection',
                action: 'manual-zero-results',
                query: dbQueryVal,
                id: MANUAL_IRI,
                listLength: 0,
                listPosition: 0,
                attempts: attemptsRef.current,
              });
              selectResult({ iri: MANUAL_IRI });
            }}
          >
            <Translate id={'request_flow.product_selection.search_zero_results_manual'} />{' '}
            <IconSmile smileDirection={'right'} />
          </div>
        </>
      )}
      {!queryValMin && (
        <SearchListZeroResults attempts={attemptsRef.current} selectResult={selectResult} locale={locale} />
      )}
    </div>
  );
};

const addBoldToLabel = (rawLabel: string, dbQueryVal: string): string => {
  // Start of with the group name
  let label = rawLabel;
  if (label.toLowerCase() === dbQueryVal.toLowerCase()) {
    label = `<span class="font-bold">${label}</span>`;
  } else {
    const matches: string[] = [];
    // We split the query on spaces, and will try to find and replace every part separately
    dbQueryVal.split(' ').forEach(queryPart => {
      // setup regex
      const re = new RegExp(queryPart, 'i');
      const match = label.match(re)?.[0];
      if (match) {
        // find and replace the partial, build the label
        matches.push(match);
      }
    });
    matches.forEach(m => {
      label = label.replace(m, `<span class="font-bold">${m}</span>`);
    });
  }
  return label;
};

let sourceSearch: CancelTokenSource | undefined = undefined;

const productSelectionSearch = (query: string): Promise<ProductSelectionResult[]> => {
  if (sourceSearch !== undefined) sourceSearch.cancel();
  sourceSearch = axios.CancelToken.source();
  return priceClient.productSelection.search({ query, cancelToken: sourceSearch.token });
};

let sourcePresetsSearch: CancelTokenSource | undefined = undefined;

const productSelectionPresetsSearch = async (
  query: string,
  accountId?: string
): Promise<ProductSelectionPresetSearchResult[]> => {
  if (!accountId) return [];

  if (sourcePresetsSearch !== undefined) sourcePresetsSearch.cancel();
  sourcePresetsSearch = axios.CancelToken.source();

  return priceClient.productSelection.searchPresets({
    accountId,
    query,
    cancelToken: sourcePresetsSearch.token,
  });
};
